import { api } from '@goaltree/backend/convex/_generated/api'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { BarChart3Icon } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AdminJourneyFeatures } from '@/components/admin-journey-features'
import { AdminNewsletter } from '@/components/admin-newsletter'
import { AdminReviewRequests, AdminReviews } from '@/components/admin-reviews'
import { AdminStats } from '@/components/admin-stats'
import { AdminStoryReports } from '@/components/admin-story-reports'
import { AppSidebar } from '@/components/app-sidebar'
import { ConsistencyStats } from '@/components/consistency-stats'
import { GrowthStats } from '@/components/growth-stats'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { ProGate } from '@/components/pro-gate'
import { ProductivityLevelCard } from '@/components/productivity-level'
import { MonthlyCards } from '@/components/share-card'
import { SiteHeader } from '@/components/site-header'
import {
  RANGES,
  type Stats,
  type StatsRange,
  StatsSkeleton,
  StatsView,
  sampleStats,
} from '@/components/stats-view'
import { useIsPro } from '@/hooks/use-is-pro'
import { useScrollTitle } from '@/hooks/use-scroll-title'

export const Route = createFileRoute('/_authenticated/stats')({
  head: () => ({
    meta: [{ title: 'Stats — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  validateSearch: (search): { view?: 'admin' } => ({
    ...(search.view === 'admin' && { view: 'admin' as const }),
  }),
  component: RouteComponent,
})

const RANGE_KEY = 'stats-range'

function useStoredRange() {
  const [range, setRange] = useState<StatsRange>(() => {
    try {
      const v = localStorage.getItem(RANGE_KEY)
      return RANGES.includes(v as StatsRange) ? (v as StatsRange) : '30d'
    } catch {
      return '30d'
    }
  })
  const update = (r: StatsRange) => {
    setRange(r)
    try {
      localStorage.setItem(RANGE_KEY, r)
    } catch {}
  }
  return [range, update] as const
}

const scrollClass = 'flex-1 overflow-y-auto pb-36 lg:pb-24'

function StatsContent({ heading }: { heading?: React.ReactNode }) {
  const { t } = useTranslation()
  const { onScroll } = useScrollTitle(t('nav.stats'))
  const [range, setRange] = useStoredRange()
  const fetched = useQuery(api.stats.getStats, { range })
  // Keep the last result on screen while a new range loads.
  const last = useRef<Stats | null>(null)
  if (fetched) last.current = fetched
  const stats = fetched ?? last.current

  if (fetched === undefined && !stats) {
    return (
      <div className={scrollClass} data-scroll-reset data-scroll-restoration-id="tab-scroll">
        <div className="px-4 pt-4 pb-4">
          <div className="mx-auto max-w-(--page-col)">
            <StatsSkeleton heading={heading} />
          </div>
        </div>
      </div>
    )
  }

  if (
    !stats ||
    (stats.goalsActive === 0 && stats.habits.length === 0 && !stats.heatmap.some((n) => n > 0))
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <BarChart3Icon className="h-12 w-12 text-muted-foreground/40" />
        <p className="text-xl font-bold text-foreground">{t('stats.noData')}</p>
        <p className="text-sm text-muted-foreground">{t('stats.noDataDesc')}</p>
        <Link
          to="/goals"
          className="mt-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white dark:text-[#0a0a0a] transition-opacity hover:opacity-90"
        >
          {t('stats.goToGoals')}
        </Link>
      </div>
    )
  }

  return (
    <div
      className={scrollClass}
      onScroll={onScroll}
      data-scroll-reset
      data-scroll-restoration-id="tab-scroll"
    >
      <div className="px-4 pt-4 pb-4">
        <div className={cn('mx-auto max-w-(--page-col)', !fetched && 'opacity-60')}>
          <div className="flex flex-col gap-4">
            <StatsView
              stats={stats}
              onRange={setRange}
              heading={heading}
              top={<ProductivityLevelCard />}
            />
            <ConsistencyStats />
            <GrowthStats />
            <MonthlyCards />
          </div>
        </div>
      </div>
    </div>
  )
}

function AdminContent({ heading }: { heading: React.ReactNode }) {
  return (
    <div className={scrollClass} data-scroll-reset data-scroll-restoration-id="tab-scroll">
      <div className="px-4 pt-4 pb-4">
        <div className="mx-auto flex max-w-(--page-col) flex-col gap-4">
          {heading}
          <AdminStoryReports />
          <AdminJourneyFeatures />
          <AdminNewsletter />
          <AdminStats />
          <AdminReviewRequests />
          <AdminReviews />
        </div>
      </div>
    </div>
  )
}

// "Your Progress | Admin" as big tab titles (like Everyday's Habits | Tasks); admins only.
function StatsTabs({ view, onView }: { view: View; onView: (v: View) => void }) {
  const { t } = useTranslation()
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {t('nav.stats')}
      </p>
      <div className="flex items-baseline gap-3">
        {(['me', 'admin'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onView(v)}
            aria-pressed={view === v}
            className={cn(
              'relative cursor-pointer rounded-md pb-2 text-3xl font-black outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50',
              view === v ? 'text-foreground' : 'text-foreground/30 hover:text-foreground/50',
            )}
          >
            {v === 'me' ? t('stats.title') : t('stats.adminTab')}
            <span
              aria-hidden
              className={cn(
                'absolute inset-x-0 bottom-0 h-[3px] rounded-full bg-primary-text transition-opacity duration-200',
                view === v ? 'opacity-100' : 'opacity-0',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  )
}

type View = 'me' | 'admin'

function RouteComponent() {
  const { t } = useTranslation()
  const { isPro, isLoading } = useIsPro()
  const isAdmin = useQuery(api.admin.amIAdmin) ?? false
  const search = Route.useSearch()
  const [view, setView] = useState<View>(search.view ?? 'me')
  const navigate = useNavigate()
  const tabs = <StatsTabs view={view} onView={setView} />

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader
          title={t('nav.stats')}
          showInbox={false}
          showCoach
          onBack={() => navigate({ to: '/today' })}
        />
        <SiteHeader className="hidden lg:flex" />
        {isAdmin && view === 'admin' ? (
          <AdminContent heading={tabs} />
        ) : isLoading ? null : !isPro ? (
          <ProGate
            preview={<StatsView stats={sampleStats(new Date().toISOString().slice(0, 10))} />}
          />
        ) : (
          <StatsContent heading={isAdmin ? tabs : undefined} />
        )}
      </SidebarInset>
    </SidebarProvider>
  )
}
